import { buildDefaultStreamlitXml } from '@/lib/streamlit-utils';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, type Editor } from '@tiptap/react';
import StreamlitAppNodeView from './streamlit-app-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        streamlitApp: {
            insertStreamlitApp: (options?: InsertStreamlitAppOptions) => ReturnType;
        };
    }
}

export interface InsertStreamlitAppOptions {
    caption?: string;
    streamlitXMLToLoad?: string;
    options?: {
        code?: string;
        guid?: string;
        streamlit_app_id?: number;
    };
}

export interface StreamlitAppNodeOptions {
    onSyncSection?: (editor: Editor) => Promise<void>;
}

export const StreamlitAppNodeHeadless = Node.create({
    name: 'streamlitApp',

    group: 'block',

    atom: true,

    selectable: true,

    draggable: true,

    addOptions(): StreamlitAppNodeOptions {
        return {
            onSyncSection: undefined,
        };
    },

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-streamlit-app-id'),
                renderHTML: attributes => ({
                    'data-streamlit-app-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
            streamlitXMLToLoad: {
                default: '',
                parseHTML: element => element.getAttribute('data-streamlit-xml') || '',
                renderHTML: attributes => ({
                    'data-streamlit-xml': attributes.streamlitXMLToLoad,
                }),
            },
            options: {
                default: {},
                parseHTML: element => {
                    const raw = element.getAttribute('data-options');
                    try {
                        return raw ? JSON.parse(raw) : {};
                    } catch (error) {
                        console.error('Failed to parse streamlit data-options:', error, raw);
                        return {};
                    }
                },
                renderHTML: attributes => ({
                    'data-options': JSON.stringify(attributes.options),
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="streamlit-app"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'streamlit-app',
                class: 'streamlit-app-container',
            }),
        ];
    },

    addCommands() {
        return {
            insertStreamlitApp:
                (options: InsertStreamlitAppOptions = {}) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            caption: options.caption || '',
                            streamlitXMLToLoad: options.streamlitXMLToLoad || buildDefaultStreamlitXml(),
                            options: options.options || { code: '' },
                        },
                    });
                },
        };
    },
});

export const StreamlitAppNode = StreamlitAppNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(StreamlitAppNodeView);
    },
});

export default StreamlitAppNode;
